import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useSiteContent } from "@/context/SiteContentContext";
import { SITE_DEFAULTS, HERO_DEFAULTS, buildHeroStyles } from "@/lib/siteDefaults";
import DashboardLayout from "@/components/DashboardLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Plus, Trash2, Upload, RotateCcw } from "lucide-react";

const SCHEMA = [
  {
    key: "general", label: "Generali & Contatti", fields: [
      { k: "logo", label: "Logo (sito pubblico e pannello laboratorio, in alto a sinistra)", type: "image" },
      { k: "panel_watermark_mode", label: "Pannello — logo in trasparenza sullo sfondo", type: "select", options: [{ v: "default", label: "Logo originale" }, { v: "logo", label: "Stesso logo di sopra" }, { v: "custom", label: "Immagine personalizzata" }] },
      { k: "panel_watermark", label: "Pannello — immagine personalizzata per lo sfondo (usata solo se scegli 'Immagine personalizzata')", type: "image" },
      { k: "phone", label: "Telefono", type: "text" },
      { k: "email", label: "Email", type: "text" },
      { k: "address", label: "Indirizzo / Sede", type: "text" },
      { k: "footer_tagline", label: "Testo footer", type: "textarea" },
      { k: "nav_home", label: "Menu — voce Home", type: "text" },
      { k: "nav_servizi", label: "Menu — voce Servizi", type: "text" },
      { k: "nav_chisiamo", label: "Menu — voce Chi siamo", type: "text" },
      { k: "nav_contatti", label: "Menu — voce Contatti", type: "text" },
      { k: "area_clienti_label", label: "Pulsante Area Clienti", type: "text" },
      { k: "footer_nav_title", label: "Footer — titolo colonna 'Naviga'", type: "text" },
      { k: "footer_contacts_title", label: "Footer — titolo colonna 'Contatti'", type: "text" },
      { k: "footer_copyright", label: "Footer — testo copyright (l'anno è automatico)", type: "text" },
    ],
  },
  {
    key: "home", label: "Home", fields: [
      { k: "hero_kicker", label: "Hero — sopratitolo", type: "text" },
      { k: "hero_title_pre", label: "Hero — titolo (prima parte)", type: "text" },
      { k: "hero_title_highlight", label: "Hero — parola evidenziata", type: "text" },
      { k: "hero_subtitle", label: "Hero — sottotitolo", type: "textarea" },
      { k: "hero_cta_primary", label: "Hero — pulsante 1", type: "text" },
      { k: "hero_cta_secondary", label: "Hero — pulsante 2", type: "text" },
      { k: "hero_video", label: "Hero — video di sfondo (MP4 esportato da Canva, loop muto; sostituisce lo sfondo animato)", type: "video" },
      { k: "hero_watermark", label: "Hero — logo in trasparenza al centro (la 'cornice' chiara dietro al testo)", type: "toggle" },
      { k: "hero_image", label: "Hero — immagine (dente 3D)", type: "image" },
      { k: "hero_settings", label: "Hero — posizione, trasparenza e cornice", type: "heroimg" },
      { k: "features", label: "Punti di forza", type: "objlist", item: [{ k: "title", label: "Titolo", type: "text" }, { k: "desc", label: "Descrizione", type: "textarea" }] },
      { k: "services_kicker", label: "Servizi — sopratitolo", type: "text" },
      { k: "services_title", label: "Servizi — titolo", type: "text" },
      { k: "stampa_title", label: "Card Stampa 3D — titolo", type: "text" },
      { k: "stampa_desc", label: "Card Stampa 3D — testo", type: "textarea" },
      { k: "stampa_image", label: "Card Stampa 3D — immagine", type: "image" },
      { k: "cad_title", label: "Card CAD — titolo", type: "text" },
      { k: "cad_desc", label: "Card CAD — testo", type: "textarea" },
      { k: "cad_image", label: "Card CAD — immagine", type: "image" },
      { k: "cta_title", label: "Banner finale — titolo", type: "text" },
      { k: "cta_subtitle", label: "Banner finale — testo", type: "textarea" },
      { k: "cta_button", label: "Banner finale — pulsante", type: "text" },
      { k: "card_link_label", label: "Card servizi — testo link ('Approfondisci')", type: "text" },
    ],
  },
  {
    key: "servizi", label: "Servizi", fields: [
      { k: "hero_kicker", label: "Sopratitolo", type: "text" },
      { k: "hero_title", label: "Titolo", type: "text" },
      { k: "hero_subtitle", label: "Sottotitolo", type: "textarea" },
      { k: "produzione_kicker", label: "Produzione — etichetta", type: "text" },
      { k: "produzione_title", label: "Produzione — titolo", type: "text" },
      { k: "produzione_tagline", label: "Produzione — riassunto", type: "textarea" },
      { k: "produzione_body", label: "Produzione — testo", type: "textarea" },
      { k: "produzione_steps", label: "Produzione — passi", type: "list" },
      { k: "produzione_cta", label: "Produzione — pulsante", type: "text" },
      { k: "formazione_kicker", label: "Formazione — etichetta", type: "text" },
      { k: "formazione_title", label: "Formazione — titolo", type: "text" },
      { k: "formazione_tagline", label: "Formazione — riassunto", type: "textarea" },
      { k: "formazione_items", label: "Formazione — elenco corsi", type: "list" },
      { k: "formazione_cta", label: "Formazione — pulsante", type: "text" },
      { k: "sviluppo_kicker", label: "Sviluppo — etichetta", type: "text" },
      { k: "sviluppo_title", label: "Sviluppo — titolo", type: "text" },
      { k: "sviluppo_tagline", label: "Sviluppo — riassunto", type: "textarea" },
      { k: "sviluppo_body", label: "Sviluppo — testo", type: "textarea" },
      { k: "sviluppo_items", label: "Sviluppo — elenco novità", type: "list" },
      { k: "sviluppo_cta", label: "Sviluppo — pulsante", type: "text" },
      { k: "final_title", label: "Banner finale — titolo", type: "text" },
      { k: "final_subtitle", label: "Banner finale — testo", type: "textarea" },
      { k: "final_cta", label: "Banner finale — pulsante", type: "text" },
    ],
  },
  {
    key: "about", label: "Chi siamo", fields: [
      { k: "hero_kicker", label: "Sopratitolo", type: "text" },
      { k: "hero_title", label: "Titolo", type: "text" },
      { k: "image", label: "Immagine (logo)", type: "image" },
      { k: "section_title", label: "Titolo sezione", type: "text" },
      { k: "para1", label: "Paragrafo 1", type: "textarea" },
      { k: "para2", label: "Paragrafo 2", type: "textarea" },
      { k: "values", label: "Valori", type: "objlist", item: [{ k: "title", label: "Titolo", type: "text" }, { k: "desc", label: "Descrizione", type: "textarea" }] },
    ],
  },
  {
    key: "contatti", label: "Contatti", fields: [
      { k: "hero_kicker", label: "Sopratitolo", type: "text" },
      { k: "hero_title", label: "Titolo", type: "text" },
      { k: "hero_subtitle", label: "Sottotitolo", type: "textarea" },
      { k: "recapiti_title", label: "Titolo blocco recapiti", type: "text" },
      { k: "recapiti_phone_label", label: "Recapiti — etichetta Telefono", type: "text" },
      { k: "recapiti_email_label", label: "Recapiti — etichetta Email", type: "text" },
      { k: "recapiti_address_label", label: "Recapiti — etichetta Sede", type: "text" },
      { k: "form_name_label", label: "Form — etichetta Nome", type: "text" },
      { k: "form_name_ph", label: "Form — placeholder Nome", type: "text" },
      { k: "form_phone_label", label: "Form — etichetta Telefono", type: "text" },
      { k: "form_phone_ph", label: "Form — placeholder Telefono", type: "text" },
      { k: "form_email_label", label: "Form — etichetta Email", type: "text" },
      { k: "form_email_ph", label: "Form — placeholder Email", type: "text" },
      { k: "form_message_label", label: "Form — etichetta Messaggio", type: "text" },
      { k: "form_message_ph", label: "Form — placeholder Messaggio", type: "text" },
      { k: "form_submit", label: "Form — pulsante invio", type: "text" },
      { k: "success_title", label: "Messaggio inviato — titolo", type: "text" },
      { k: "success_text", label: "Messaggio inviato — testo", type: "textarea" },
      { k: "success_button", label: "Messaggio inviato — pulsante", type: "text" },
    ],
  },
  {
    key: "announcement", label: "Avviso pop-up", fields: [
      { k: "enabled", label: "Avviso attivo (appare all'arrivo sul sito e nell'area clienti)", type: "toggle" },
      { k: "video", label: "Video promozionale (mp4) — parte prima della locandina, poi scende la locandina", type: "video" },
      { k: "image", label: "Immagine dell'avviso (es. locandina chiusura / fiera)", type: "image" },
      { k: "title", label: "Testo breve sotto l'immagine (facoltativo)", type: "text", ph: "Es. Chiusura dal 14 al 17 ottobre" },
      { k: "link", label: "Link al click sull'immagine (facoltativo)", type: "text", ph: "https://..." },
      { k: "start_date", label: "Mostra dal giorno (facoltativo)", type: "date" },
      { k: "end_date", label: "Nascondi dopo il giorno (facoltativo)", type: "date" },
    ],
  },
];

function ImageField({ value, resolveImg, onChange, video }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (video && file.size > 200 * 1024 * 1024) { toast.error("Video troppo grande (max 200MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/site-content/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success(video ? "Video caricato" : "Immagine caricata");
    } catch {
      toast.error(video ? "Errore nel caricamento video" : "Errore nel caricamento immagine");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className={`${video ? "h-20 w-32" : "h-20 w-20"} rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0`}>
        {value ? (video ? <video src={resolveImg(value)} muted className="max-h-full max-w-full" /> : <img src={resolveImg(value)} alt="" className="max-h-full max-w-full object-contain" />) : <span className="text-xs text-slate-400">—</span>}
      </div>
      <div className="flex-1 space-y-2">
        <input ref={inputRef} type="file" accept={video ? "video/mp4,video/webm,video/quicktime" : "image/*"} className="hidden" onChange={pick} />
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid={video ? "upload-video-button" : undefined}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />} {video ? "Carica video (mp4)" : "Cambia immagine"}
          </Button>
          {video && value && <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} className="text-red-600">Rimuovi video</Button>}
        </div>
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={video ? "URL video" : "URL immagine"} className="text-xs" />
      </div>
    </div>
  );
}

function HeroImageSettings({ value, imageUrl, resolveImg, onChange }) {
  const s = { ...HERO_DEFAULTS, ...(value || {}) };
  const set = (k, v) => onChange({ ...s, [k]: v });
  const styles = buildHeroStyles(s);

  const Slider = ({ label, k, min, max, step = 1, suffix = "" }) => (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-slate-500"><span>{label}</span><span className="font-mono text-slate-700">{s[k]}{suffix}</span></div>
      <input type="range" min={min} max={max} step={step} value={s[k]} onChange={(e) => set(k, Number(e.target.value))} className="w-full accent-teal-600" data-testid={`hero-${k}`} />
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="relative overflow-hidden rounded-xl border border-slate-200" style={{ height: 260, background: "radial-gradient(circle at 30% 50%, #134e4a, #0f172a)" }} data-testid="hero-preview">
        {imageUrl && (
          <div className="absolute -translate-y-1/2 overflow-hidden" style={styles.outer}>
            <div className="w-full h-full" style={styles.mask}>
              <img src={resolveImg(imageUrl)} alt="" className="w-full h-full object-cover mix-blend-screen" style={styles.img} />
            </div>
          </div>
        )}
        <div className="absolute left-4 top-4 text-white/80 text-xs font-semibold uppercase tracking-wider pointer-events-none">Anteprima</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        <Slider label="Posizione orizzontale" k="offset_x" min={-30} max={30} suffix="%" />
        <Slider label="Posizione verticale" k="offset_y" min={-50} max={50} suffix="%" />
        <Slider label="Larghezza" k="width" min={30} max={100} suffix="%" />
        <Slider label="Altezza" k="height" min={40} max={120} suffix="%" />
        <Slider label="Zoom immagine" k="scale" min={80} max={170} suffix="%" />
        <Slider label="Inquadratura orizzontale" k="pos_x" min={0} max={100} suffix="%" />
        <Slider label="Inquadratura verticale" k="pos_y" min={0} max={100} suffix="%" />
        <Slider label="Dissolvenza laterale (sinistra)" k="fade_side" min={0} max={60} suffix="%" />
        <Slider label="Dissolvenza alto/basso" k="fade_edge" min={0} max={45} suffix="%" />
      </div>

      <div className="rounded-xl border border-slate-200 p-4 space-y-4 bg-slate-50/50">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={!!s.frame} onChange={(e) => set("frame", e.target.checked)} className="h-4 w-4 accent-teal-600" data-testid="hero-frame-toggle" />
          <span className="text-sm font-semibold text-slate-700">Mostra cornice</span>
        </label>
        {s.frame && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">Colore cornice</Label>
              <input type="color" value={s.frame_color} onChange={(e) => set("frame_color", e.target.value)} className="h-9 w-full rounded-md border border-slate-200 cursor-pointer" data-testid="hero-frame-color" />
            </div>
            <Slider label="Spessore cornice" k="frame_width" min={0} max={14} suffix="px" />
            <Slider label="Angoli arrotondati" k="frame_radius" min={0} max={60} suffix="px" />
          </div>
        )}
      </div>
    </div>
  );
}

function SimpleInput({ field, value, onChange }) {
  if (field.type === "textarea") {
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} />;
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

export default function GestioneSitoPage() {
  const { content, loaded, refresh, resolveImg } = useSiteContent();
  const [data, setData] = useState(null);
  const [active, setActive] = useState("general");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loaded && !data) setData(JSON.parse(JSON.stringify(content)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  if (!data) {
    return (
      <DashboardLayout title="Gestione Sito" subtitle="Modifica testi e immagini del sito pubblico.">
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
      </DashboardLayout>
    );
  }

  const section = SCHEMA.find((s) => s.key === active);

  const setField = (key, val) =>
    setData((d) => ({ ...d, [active]: { ...d[active], [key]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/site-content", { content: data });
      await refresh();
      toast.success("Contenuti salvati. Il sito è aggiornato.");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const resetSection = () => {
    setData((d) => ({ ...d, [active]: JSON.parse(JSON.stringify(SITE_DEFAULTS[active])) }));
    toast.info("Sezione riportata ai valori originali (ricordati di salvare).");
  };

  return (
    <DashboardLayout
      title="Gestione Sito"
      subtitle="Modifica testi e immagini del sito pubblico. Le modifiche vanno online al salvataggio."
      actions={
        <Button data-testid="save-site-content" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Salva modifiche
        </Button>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Card className="p-2 border-slate-200 sticky top-20">
            {SCHEMA.map((s) => (
              <button
                key={s.key}
                data-testid={`section-${s.key}`}
                onClick={() => setActive(s.key)}
                className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${active === s.key ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-teal-50"}`}
              >
                {s.label}
              </button>
            ))}
          </Card>
        </div>

        <div className="lg:col-span-3">
          <Card className="p-6 border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-lg font-semibold text-slate-900">{section.label}</h3>
              <Button type="button" variant="ghost" size="sm" onClick={resetSection} className="text-slate-500">
                <RotateCcw className="h-4 w-4 mr-1.5" /> Ripristina sezione
              </Button>
            </div>

            <div className="space-y-6">
              {section.fields.map((f) => {
                const value = data[active]?.[f.k];
                return (
                  <div key={f.k} className="space-y-2">
                    <Label className="text-slate-700">{f.label}</Label>

                    {f.type === "image" && (
                      <ImageField value={value} resolveImg={resolveImg} onChange={(v) => setField(f.k, v)} />
                    )}

                    {f.type === "video" && (
                      <ImageField video value={value} resolveImg={resolveImg} onChange={(v) => setField(f.k, v)} />
                    )}

                    {f.type === "toggle" && (
                      <div className="flex items-center gap-3">
                        <Switch data-testid={`toggle-${f.k}`} checked={!!value} onCheckedChange={(v) => setField(f.k, v)} />
                        <span className={`text-sm font-medium ${value ? "text-teal-700" : "text-slate-400"}`}>{value ? "Attivo" : "Disattivato"}</span>
                      </div>
                    )}

                    {f.type === "select" && (
                      <div className="flex flex-wrap gap-2">
                        {f.options.map((o) => (
                          <button key={o.v} type="button" data-testid={`select-${f.k}-${o.v}`} onClick={() => setField(f.k, o.v)}
                            className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${(value || f.options[0].v) === o.v ? "bg-teal-600 text-white border-teal-600" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400"}`}>
                            {o.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {f.type === "date" && (
                      <Input type="date" data-testid={`date-${f.k}`} value={value || ""} onChange={(e) => setField(f.k, e.target.value)} className="max-w-xs" />
                    )}

                    {f.type === "heroimg" && (
                      <HeroImageSettings value={value} imageUrl={data.home?.hero_image} resolveImg={resolveImg} onChange={(v) => setField(f.k, v)} />
                    )}

                    {(f.type === "text" || f.type === "textarea") && (
                      <SimpleInput field={f} value={value} onChange={(v) => setField(f.k, v)} />
                    )}

                    {f.type === "list" && (
                      <div className="space-y-2">
                        {(value || []).map((it, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input value={it} onChange={(e) => {
                              const arr = [...value]; arr[idx] = e.target.value; setField(f.k, arr);
                            }} />
                            <Button type="button" variant="ghost" size="sm" className="text-rose-500 shrink-0" onClick={() => setField(f.k, value.filter((_, i) => i !== idx))}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={() => setField(f.k, [...(value || []), ""])}>
                          <Plus className="h-4 w-4 mr-1.5" /> Aggiungi voce
                        </Button>
                      </div>
                    )}

                    {f.type === "objlist" && (
                      <div className="space-y-4">
                        {(value || []).map((obj, idx) => (
                          <div key={idx} className="rounded-lg border border-slate-200 p-4 space-y-3 bg-slate-50/50">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">#{idx + 1}</span>
                              <Button type="button" variant="ghost" size="sm" className="text-rose-500" onClick={() => setField(f.k, value.filter((_, i) => i !== idx))}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                            {f.item.map((sf) => (
                              <div key={sf.k} className="space-y-1.5">
                                <Label className="text-xs text-slate-500">{sf.label}</Label>
                                <SimpleInput field={sf} value={obj[sf.k]} onChange={(v) => {
                                  const arr = value.map((o, i) => i === idx ? { ...o, [sf.k]: v } : o);
                                  setField(f.k, arr);
                                }} />
                              </div>
                            ))}
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={() => {
                          const empty = {}; f.item.forEach((sf) => (empty[sf.k] = ""));
                          setField(f.k, [...(value || []), empty]);
                        }}>
                          <Plus className="h-4 w-4 mr-1.5" /> Aggiungi elemento
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 pt-6 border-t border-slate-100">
              <Button data-testid="save-site-content-bottom" onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Salva modifiche
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
